<div class="table-container" #tableTop [class.loading]="isLoadingDialog">
  <!-- Loading Overlay -->
  @if (isLoadingDialog) {
    <div class="loading-overlay">
      <mat-spinner diameter="48"></mat-spinner>
      <span class="loading-text">Betöltés...</span>
    </div>
  }
  
  <!-- Table Header -->
  <div class="table-header">
    <div class="table-title">
      <h2>{{title}}</h2>
    </div>
  </div>

  <!-- Assignment Actions - Hidden for Call Center employees -->
  @if (!isCallCenterEmployee) {
    <div class="assignment-actions-bar" 
         [class.has-selection]="selection.hasValue()">
      <div class="assignment-controls">
        <button mat-icon-button 
                [matMenuTriggerFor]="assignmentMenu" 
                matTooltip="Munkatárs hozzárendelése"
                [disabled]="!selection.hasValue()"
                class="assignment-button"
                [class.disabled]="!selection.hasValue()">
          <mat-icon>assignment_ind</mat-icon>
        </button>
        
        @if (selection.hasValue()) {
          <span class="selection-info">
            {{ selection.selected.length }} sor kiválasztva
          </span>
        }
        
        <mat-menu #assignmentMenu="matMenu">
          @for (user of callCenterUsers; track user.id) {
            <button mat-menu-item 
                    (click)="onAssigneeSelected(user)">
              <mat-icon>person</mat-icon>
              <span>{{ user.username }}</span>
            </button>
          }
          @if (callCenterUsers.length > 0) {
            <mat-divider></mat-divider>
          }
          <button mat-menu-item 
                  (click)="removeSelectedAssignments()"
                  [disabled]="!selection.hasValue()">
            <mat-icon>clear</mat-icon>
            <span>Hozzárendelés törlése</span>
          </button>
        </mat-menu>
      </div>
    </div>
  }

  <!-- Data Table Desktop View -->
  <div class="table-wrapper desktop-view">
    <table mat-table [dataSource]="dataSource" matSort class="data-table">
      
      <!-- Checkbox Column -->
      <ng-container matColumnDef="select">
        <th mat-header-cell *matHeaderCellDef>
          <mat-checkbox 
            (change)="$event ? toggleAllRows() : null"
            [checked]="selection.hasValue() && isAllSelected()"
            [indeterminate]="selection.hasValue() && !isAllSelected()">
          </mat-checkbox>
        </th>
        <td mat-cell *matCellDef="let row">
          <mat-checkbox 
            (click)="$event.stopPropagation()"
            (change)="$event ? selection.toggle(row) : null"
            [checked]="selection.isSelected(row)">
          </mat-checkbox>
        </td>
      </ng-container>

      <!-- Date Column -->
      <ng-container matColumnDef="date">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="date">Dátum / Hívásszám</th>
        <td mat-cell *matCellDef="let element">
          <div class="date-cell">
            @if (element.date) {
              <div class="date-time">{{element.date | date:'yyyy.MM.dd HH:mm'}}</div>
            }
            <div class="call-count">Hívás: {{element.callCount}}</div>
          </div>
        </td>
      </ng-container>

      <!-- Notes Column -->
      <ng-container matColumnDef="notes">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="assigneddate">Megjegyzés / Hozzárendelés dátum</th>
        <td mat-cell *matCellDef="let element">
          <div class="notes-cell">
            @if (element.notes?.startsWith('assignment_ind ')) {
              <span class="assignment-info">
                <mat-icon class="assignment-icon">assignment_ind</mat-icon>
                <span class="assigned-person">{{element.notes.substring('assignment_ind '.length).split('\n')[0]}}</span>
                @if (element.assignedDate) {
                  <span class="assigned-date">{{element.assignedDate | date:'yyyy. MM. dd.'}}</span>
                }
              </span>
              @if (element.notes.includes('\n')) {
                <span class="notes-text">
                  <br>{{element.notes.substring(element.notes.indexOf('\n') + 1)}}
                </span>
              }
            } @else {
              {{element.notes}}
            }
          </div>
        </td>
      </ng-container>

      <!-- Status Column -->
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="status">Státusz</th>
        <td mat-cell *matCellDef="let element">
          <app-call-center-chip 
            [config]="getStatusChipConfig(element)"
            (click)="editStatus(element, $event)">
          </app-call-center-chip>
        </td>
      </ng-container>

      <!-- Company Info Column -->
      <ng-container matColumnDef="companyInfo">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="companyName">Céginformációk</th>
        <td mat-cell *matCellDef="let element">
          <div class="company-cell">
            <div class="company-name clickable-chip" 
                 (click)="copyToClipboard(element.companyName, $event)">
              <mat-icon class="company-icon">business</mat-icon>
              <strong>{{element.companyName}}</strong>
            </div>
            <div class="company-details">
              <div class="registration-number">{{element.registrationNumber}}</div>
              <div class="main-activity">{{element.mainActivity}}</div>
              <div class="location-info">{{element.postalCode}} - {{element.county}}</div>
            </div>
          </div>
        </td>
      </ng-container>

      <!-- Officials Column -->
      <ng-container matColumnDef="officials">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="officials">Döntéshozók</th>
        <td mat-cell *matCellDef="let element">
          <div class="officials-cell">
            @for (chipConfig of getOfficialChipConfigs(element); track chipConfig.text) {
              <app-call-center-chip 
                [config]="chipConfig"
                (click)="copyToClipboard(chipConfig.text, $event)">
              </app-call-center-chip>
            }
          </div>
        </td>
      </ng-container>

      <!-- Phone Numbers Column -->
      <ng-container matColumnDef="phoneNumbers">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="phoneNumbers">Elérhetőségek</th>
        <td mat-cell *matCellDef="let element">
          <div class="contact-cell">
            <div class="phone-chips">
              @for (chipConfig of getPhoneChipConfigs(element); track chipConfig.text) {
                <app-call-center-chip 
                  [config]="chipConfig"
                  (click)="copyToClipboard(chipConfig.text, $event)">
                </app-call-center-chip>
              }
            </div>
            
            <div class="email-chips">
              @if (element.emails && element.emails.length > 0) {
                @for (chipConfig of getEmailChipConfigs(element); track chipConfig.text) {
                  <app-call-center-chip 
                    [config]="chipConfig"
                    (click)="copyToClipboard(chipConfig.text, $event)">
                  </app-call-center-chip>
                }
              } @else {
                <span class="no-data">Nincs email</span>
              }
            </div>
          </div>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
    </table>
  </div>

  <!-- Mobile Card View -->
  <div class="mobile-view">
    @for (element of paginatedData; track element.id) {
      <div class="mobile-card">
        <div class="card-header">
          @if (!isCallCenterEmployee) {
            <mat-checkbox 
              (click)="$event.stopPropagation()"
              (change)="$event ? selection.toggle(element) : null"
              [checked]="selection.isSelected(element)"
              class="card-checkbox">
            </mat-checkbox>
          }
          <div class="card-title">
            <mat-icon class="company-icon">business</mat-icon>
            <strong>{{element.companyName}}</strong>
          </div>
          <app-call-center-chip 
            [config]="getStatusChipConfig(element)"
            (click)="editStatus(element, $event)">
          </app-call-center-chip>
        </div>

        <div class="card-content">
          <!-- Company Information -->
          <div class="card-section">
            <div class="section-label">Céginformációk</div>
            <div class="company-details">
              <div class="registration-number">{{element.registrationNumber}}</div>
              <div class="main-activity">{{element.mainActivity}}</div>
              <div class="location-info">{{element.postalCode}} - {{element.county}}</div>
            </div>
          </div>

          <!-- Date and Call Count -->
          <div class="card-section">
            <div class="section-label">Dátum / Hívásszám</div>
            <div class="date-info">
              @if (element.date) {
                <div class="date-time">{{element.date | date:'yyyy.MM.dd HH:mm'}}</div>
              }
              <div class="call-count">Hívás: {{element.callCount}}</div>
            </div>
          </div>

          <!-- Officials -->
          @if (getOfficialChipConfigs(element).length > 0) {
            <div class="card-section">
              <div class="section-label">Döntéshozók</div>
              <div class="officials-chips">
                @for (chipConfig of getOfficialChipConfigs(element); track chipConfig.text) {
                  <app-call-center-chip 
                    [config]="chipConfig"
                    (click)="copyToClipboard(chipConfig.text, $event)">
                  </app-call-center-chip>
                }
              </div>
            </div>
          }

          <!-- Contact Information -->
          <div class="card-section">
            <div class="section-label">Elérhetőségek</div>
            <div class="contact-info">
              <!-- Phone Numbers -->
              @if (getPhoneChipConfigs(element).length > 0) {
                <div class="phone-chips">
                  @for (chipConfig of getPhoneChipConfigs(element); track chipConfig.text) {
                    <app-call-center-chip 
                      [config]="chipConfig"
                      (click)="copyToClipboard(chipConfig.text, $event)">
                    </app-call-center-chip>
                  }
                </div>
              }
              
              <!-- Email Addresses -->
              @if (element.emails && element.emails.length > 0) {
                <div class="email-chips">
                  @for (chipConfig of getEmailChipConfigs(element); track chipConfig.text) {
                    <app-call-center-chip 
                      [config]="chipConfig"
                      (click)="copyToClipboard(chipConfig.text, $event)">
                    </app-call-center-chip>
                  }
                </div>
              } @else {
                <div class="no-data">Nincs email</div>
              }
            </div>
          </div>

          <!-- Notes -->
          @if (element.notes) {
            <div class="card-section">
              <div class="section-label">Megjegyzés</div>
              <div class="notes-content">
                @if (element.notes.startsWith('assignment_ind ')) {
                  <span class="assignment-info">
                    <mat-icon class="assignment-icon">assignment_ind</mat-icon>
                    <span class="assigned-person">{{element.notes.substring('assignment_ind '.length).split('\n')[0]}}</span>
                    @if (element.assignedDate) {
                      <span class="assigned-date">{{element.assignedDate | date:'yyyy. MM. dd.'}}</span>
                    }
                  </span>
                  @if (element.notes.includes('\n')) {
                    <span class="notes-text">
                      <br>{{element.notes.substring(element.notes.indexOf('\n') + 1)}}
                    </span>
                  }
                } @else {
                  {{element.notes}}
                }
              </div>
            </div>
          }
        </div>
      </div>
    }
  </div>

  <!-- Paginator - szerver oldali lapozás -->
  @if (!hidePaginator) {
    <mat-paginator
      [length]="totalRecords"
      [pageSizeOptions]="[10, 25, 50, 100]"
      [pageSize]="10"
      showFirstLastButtons>
    </mat-paginator>
  }
</div>
